import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, ChevronDown, Download, FileCheck2, FileText, Image as ImageIcon, Languages, UploadCloud, UserRound } from 'lucide-react';
import api from '../services/api';
import ClientOfferPricingSection, { DEFAULT_PRICING, evaluatePricing, initialPricing } from './ClientOfferPricingSection';
import ClientOfferMontageSection, { CUSTOM_POSITION, montagePositionLabel, montageRowsInvalid } from './ClientOfferMontageSection';
import './css/ClientAluminiumOffers.css';

const DEFAULT_COMPANY = {
  company_name: 'AT-Fenster GmbH & Co. KG',
  company_street: 'Minervastr. 50',
  company_city: '46419 Isselburg',
  company_phone: '02874/9053698',
  company_fax: '-',
  company_email: 'info@at-fenster.de',
};
const EMPTY_CLIENT = { name: '', street: '', city: '', phone: '', fax: '', email: '' };
const DEFAULT_OPTIONS = { include_logo: true, include_company: true, include_recipient: true };
const TRANSLATION_KINDS = {
  'overview-title': 'Nagłówek opisu', overview: 'Opis ogólny',
  caption: 'Podpis konstrukcji', remarks: 'Uwagi', callout: 'Tekst konstrukcji',
};

const fields = [
  { key: 'street', label: 'Ulica', maxLength: 150 },
  { key: 'city', label: 'Kod i miasto', maxLength: 150 },
  { key: 'phone', label: 'Telefon', maxLength: 80 },
  { key: 'fax', label: 'Faks', maxLength: 80 },
  { key: 'email', label: 'E-mail', maxLength: 150, type: 'email' },
];

const getError = error => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => item.msg).join(' ');
  return 'Nie udało się przetworzyć oferty. Spróbuj ponownie.';
};

export default function ClientAluminiumOffers() {
  const [file, setFile] = useState(null);
  const [company, setCompany] = useState(DEFAULT_COMPANY);
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [expandedSections, setExpandedSections] = useState({
    company: true, recipient: true, translation: true, pricing: true, montage: true,
  });
  const [state, setState] = useState('empty');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('de');
  const [translationRows, setTranslationRows] = useState([]);
  const [translationApplied, setTranslationApplied] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translationStatus, setTranslationStatus] = useState('');
  const [translationError, setTranslationError] = useState(false);
  const [pricingAnalysis, setPricingAnalysis] = useState(null);
  const [pricingError, setPricingError] = useState('');
  const [pricingSettings, setPricingSettings] = useState(DEFAULT_PRICING);
  const [montageEnabled, setMontageEnabled] = useState(false);
  const [montageRows, setMontageRows] = useState([]);
  const previewRef = useRef('');
  const requestId = useRef(0);
  const translationRequestId = useRef(0);
  const pricingEvaluation = useMemo(
    () => evaluatePricing(pricingAnalysis, pricingSettings),
    [pricingAnalysis, pricingSettings],
  );
  const montagePositions = useMemo(() => {
    const seen = new Set();
    return translationRows.filter(row => {
      if (row.kind !== 'caption' || seen.has(row.construction)) return false;
      seen.add(row.construction);
      return true;
    }).map(row => ({
      id: row.construction,
      label: (row.translated || row.original).replace(/\s+/g, ' ').trim(),
    }));
  }, [translationRows]);
  const montageInvalid = montageRowsInvalid(montageEnabled, montagePositions, montageRows);

  useEffect(() => () => {
    requestId.current += 1;
    translationRequestId.current += 1;
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
  }, []);

  const clearPreview = () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = '';
    setPreviewUrl('');
  };

  const selectFile = async event => {
    const selected = event.target.files?.[0] || null;
    const currentRequest = ++requestId.current;
    translationRequestId.current += 1;
    clearPreview();
    setFile(selected);
    setCompany(DEFAULT_COMPANY);
    setClient(EMPTY_CLIENT);
    setOptions(DEFAULT_OPTIONS);
    setExpandedSections({ company: true, recipient: true, translation: true, pricing: true, montage: true });
    setTargetLanguage('de');
    setTranslationRows([]);
    setTranslationApplied(false);
    setTranslating(false);
    setTranslationStatus('');
    setTranslationError(false);
    setPricingAnalysis(null);
    setPricingError('');
    setPricingSettings(DEFAULT_PRICING);
    setMontageEnabled(false);
    setMontageRows([]);
    setMessage('');
    setIsError(false);
    if (!selected) {
      setState('empty');
      return;
    }
    if (!selected.name.toLowerCase().endsWith('.pdf') || selected.size > 30 * 1024 * 1024) {
      setState('invalid');
      setIsError(true);
      setMessage('Wybierz ofertę PDF o rozmiarze do 30 MB.');
      return;
    }
    setState('checking');
    const data = new FormData();
    data.append('offer', selected);
    try {
      const response = await api.post('/clients/aluminium-offers/inspect', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (currentRequest !== requestId.current) return;
      const rows = response.data.translation?.rows || [];
      setTranslationRows(rows.map(row => ({
        id: row.id, kind: row.kind, construction: row.construction,
        original: row.text, translated: row.text,
      })));
      const positions = rows.filter((row, index) => row.kind === 'caption'
        && rows.findIndex(candidate => candidate.kind === 'caption'
          && candidate.construction === row.construction) === index);
      setMontageRows(positions.map((row, index) => ({
        id: index + 1, position: row.construction,
        position_label: row.text.replace(/\s+/g, ' ').trim(),
        position_label_edited: false,
        installation_type: '', description: '', net_price: '',
      })));
      setPricingAnalysis(response.data.pricing || null);
      setPricingError(response.data.pricing_error || '');
      setPricingSettings(initialPricing(response.data.pricing));
      setState('ready');
      setMessage(`Struktura oferty jest prawidłowa. Wykryto ${rows.length} tekstów do tłumaczenia.`);
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      setState('invalid');
      setIsError(true);
      setMessage(getError(error));
    }
  };

  const updateCompany = (key, value) => {
    clearPreview();
    setMessage('Dane zmieniono. Zapisz ofertę ponownie.');
    setIsError(false);
    setCompany(current => ({ ...current, [key]: value }));
  };
  const updateClient = (key, value) => {
    clearPreview();
    setMessage('Dane zmieniono. Zapisz ofertę ponownie.');
    setIsError(false);
    setClient(current => ({ ...current, [key]: value }));
  };
  const updateOption = (key, checked) => {
    clearPreview();
    setMessage('Ustawienia zmieniono. Przygotuj ofertę ponownie.');
    setIsError(false);
    setOptions(current => ({ ...current, [key]: checked }));
  };
  const updateMontageEnabled = checked => {
    clearPreview();
    setMontageEnabled(checked);
    if (checked && !montageRows.length) {
      setMontageRows([{
        id: 1, position: montagePositions[0]?.id || CUSTOM_POSITION,
        position_label: montagePositions[0]?.label || '',
        position_label_edited: !montagePositions.length,
        installation_type: '', description: '', net_price: '',
      }]);
    }
  };
  const updateMontageRows = updater => {
    clearPreview();
    setMontageRows(updater);
  };
  const toggleSection = section => {
    setExpandedSections(current => ({ ...current, [section]: !current[section] }));
  };

  const updateTranslation = (id, key, value) => {
    translationRequestId.current += 1;
    setTranslating(false);
    clearPreview();
    setTranslationRows(current => current.map(row => {
      if (row.id !== id) return row;
      return key === 'original'
        ? { ...row, original: value, translated: value }
        : { ...row, translated: value };
    }));
    if (key === 'translated') setTranslationApplied(true);
    setTranslationStatus('Teksty zmieniono. Przygotuj ofertę ponownie.');
    setTranslationError(false);
  };

  const changeLanguage = value => {
    translationRequestId.current += 1;
    setTranslating(false);
    setTargetLanguage(value);
    setTranslationRows(current => current.map(row => ({ ...row, translated: row.original })));
    setTranslationApplied(false);
    setTranslationStatus('Wybierz „Przetłumacz”, aby przygotować teksty w wybranym języku.');
    setTranslationError(false);
    clearPreview();
  };

  const updatePricing = patch => {
    clearPreview();
    setMessage('Parametry rabatowania zmieniono. Przygotuj ofertę ponownie.');
    setIsError(false);
    setPricingSettings(current => ({ ...current, ...patch }));
  };

  const translate = async () => {
    if (!translationRows.length || translating || state !== 'ready') return;
    const currentRequest = ++translationRequestId.current;
    setTranslating(true);
    setTranslationStatus('Tłumaczę teksty oferty…');
    setTranslationError(false);
    try {
      const response = await api.post('/clients/aluminium-offers/translate', {
        target_language: targetLanguage,
        rows: translationRows.map(row => ({ id: row.id, text: row.original })),
      });
      if (currentRequest !== translationRequestId.current) return;
      if (!Array.isArray(response.data?.translations)) {
        throw new Error('Nie otrzymano tłumaczeń dla wszystkich tekstów.');
      }
      const translated = new Map(response.data.translations.map(row => [row.id, row.text]));
      if (translated.size !== translationRows.length || translationRows.some(row => !translated.get(row.id))) {
        throw new Error('Nie otrzymano tłumaczeń dla wszystkich tekstów.');
      }
      setTranslationRows(current => current.map(row => ({ ...row, translated: translated.get(row.id) })));
      setTranslationApplied(true);
      clearPreview();
      setTranslationStatus('Tłumaczenia wpisano do pól. Możesz je poprawić przed przygotowaniem PDF.');
    } catch (error) {
      if (currentRequest !== translationRequestId.current) return;
      setTranslationStatus(error.response ? getError(error) : error.message);
      setTranslationError(true);
    } finally {
      if (currentRequest === translationRequestId.current) setTranslating(false);
    }
  };

  const apply = async event => {
    event.preventDefault();
    if (!file || state !== 'ready' || translating || pricingEvaluation.invalid || montageInvalid) return;
    const currentRequest = ++requestId.current;
    clearPreview();
    setState('saving');
    setIsError(false);
    setMessage('Przygotowuję zmodyfikowaną ofertę…');
    const data = new FormData();
    data.append('offer', file);
    data.append('montage_language', targetLanguage);
    Object.entries(options).forEach(([key, value]) => data.append(key, String(value)));
    Object.entries({ ...company, ...client }).forEach(([key, value]) => data.append(key, value));
    if (translationApplied) {
      data.append('translations', JSON.stringify(Object.fromEntries(
        translationRows.map(row => [row.id, row.translated]),
      )));
    }
    if (pricingSettings.enabled) {
      data.append('pricing', JSON.stringify({
        workflow_mode: pricingSettings.workflowMode,
        mode: pricingSettings.mode,
        vat_percent: pricingSettings.vat,
        discounts: pricingSettings.discounts,
        general_discount: pricingSettings.generalDiscount,
        target_gross: pricingSettings.targetGross,
        markup_mode: pricingSettings.markupMode,
        markups: pricingSettings.markups,
        general_markup: pricingSettings.generalMarkup,
        markup_target_gross: pricingSettings.markupTargetGross,
      }));
    }
    if (montageEnabled) {
      data.append('montage', JSON.stringify(montageRows.map(row => ({
        position: row.position,
        position_label: montagePositionLabel(row, montagePositions),
        installation_type: row.installation_type,
        description: row.description,
        net_price: row.net_price,
      }))));
    }
    try {
      const response = await api.post('/clients/aluminium-offers/apply', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      });
      if (currentRequest !== requestId.current) return;
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      previewRef.current = url;
      setPreviewUrl(url);
      setState('ready');
      setMessage('Oferta jest gotowa. Sprawdź podgląd i pobierz plik PDF.');
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      let displayedError = error;
      if (error.response?.data instanceof Blob) {
        try {
          displayedError = { response: { data: JSON.parse(await error.response.data.text()) } };
        } catch { /* Show the generic error. */ }
      }
      setState('ready');
      setIsError(true);
      setMessage(getError(displayedError));
    }
  };

  const companyFields = [
    { key: 'company_name', label: 'Nazwa firmy', maxLength: 150 },
    ...fields.map(field => ({ ...field, key: `company_${field.key}` })),
  ];

  return (
    <div className="page-container client-offers">
      <header className="client-offers-hero">
        <div className="client-offers-eyebrow">Klienci / Automatyzacja ofert</div>
        <h2>Oferty aluminium</h2>
        <p>Uzupełnij dane niemieckiej oferty PDF, przetłumacz opisy i przelicz ceny.</p>
      </header>
      <div className="client-offers-content">
        <section className="client-offers-card client-offers-source">
          <div className="client-offers-card-heading">
            <span className="client-offers-card-icon"><UploadCloud size={23} aria-hidden="true" /></span>
            <div><h3>Oferta źródłowa</h3><p>Załącz niemiecką ofertę PDF. Maksymalny rozmiar pliku: 30 MB.</p></div>
          </div>
          <div className="client-offers-upload">
            <span className="client-offers-file-icon"><FileText size={25} aria-hidden="true" /></span>
            <span className="client-offers-file-info"><strong>{file?.name || 'Nie wybrano pliku'}</strong><small>{file ? 'Oferta zostanie sprawdzona przed edycją' : 'Wybierz plik PDF, aby rozpocząć'}</small></span>
            <label className="client-offers-file-button" htmlFor="client-offers-file">{file ? 'Zmień plik' : 'Wybierz PDF'}</label>
            <input id="client-offers-file" type="file" accept="application/pdf,.pdf" onChange={selectFile} aria-label="Oferta aluminium PDF" />
          </div>
          {message && <p className={`client-offers-status ${isError ? 'error' : ''}`} role="status">{state === 'ready' && !isError && <FileCheck2 size={18} aria-hidden="true" />}{message}</p>}
        </section>

        {(state === 'ready' || state === 'saving') && (
          <form onSubmit={apply} className="client-offers-form">
            <div className="client-offers-form-heading"><h3>Edycja informacji ogólnych</h3><p>Po uzupełnieniu danych możesz przetłumaczyć opisy i zmienić ceny.</p></div>

            <section className="client-offers-card client-offers-step" aria-labelledby="client-offers-logo-title">
              <div className="client-offers-step-heading">
                <span className="client-offers-number">1</span>
                <span className="client-offers-step-icon"><ImageIcon size={21} aria-hidden="true" /></span>
                <div className="client-offers-step-title"><h4 id="client-offers-logo-title">Logo</h4><p>Logo AT-Fenster na pierwszej stronie oferty.</p></div>
                <label className="client-offers-toggle"><input type="checkbox" aria-label="Dodaj logo" checked={options.include_logo} disabled={state === 'saving'} onChange={event => updateOption('include_logo', event.target.checked)} /><span className="client-offers-toggle-track" aria-hidden="true" /><span>{options.include_logo ? 'Włączone' : 'Wyłączone'}</span></label>
              </div>
            </section>

            <section className="client-offers-card client-offers-step" aria-labelledby="client-offers-company-title">
              <div className="client-offers-step-heading">
                <span className="client-offers-number">2</span>
                <span className="client-offers-step-icon"><Building2 size={21} aria-hidden="true" /></span>
                <div className="client-offers-step-title"><h4 id="client-offers-company-title">Informacje o firmie</h4><p>{options.include_company ? 'Dane AT-Fenster są uzupełnione domyślnie.' : 'Dane firmy zostaną usunięte z oferty.'}</p></div>
                <label className="client-offers-toggle"><input type="checkbox" aria-label="Uwzględnij informacje o firmie" checked={options.include_company} disabled={state === 'saving'} onChange={event => updateOption('include_company', event.target.checked)} /><span className="client-offers-toggle-track" aria-hidden="true" /><span>{options.include_company ? 'Włączone' : 'Wyłączone'}</span></label>
                <button className={`client-offers-collapse ${expandedSections.company ? 'open' : ''}`} type="button" aria-label={`${expandedSections.company ? 'Zwiń' : 'Rozwiń'} informacje o firmie`} aria-expanded={expandedSections.company} aria-controls="client-offers-company-fields" onClick={() => toggleSection('company')}><ChevronDown size={20} aria-hidden="true" /></button>
              </div>
              <div id="client-offers-company-fields" hidden={!expandedSections.company} className={`client-offers-grid ${!options.include_company ? 'client-offers-grid-disabled' : ''}`}>
                {companyFields.map(field => <label key={field.key}>{field.label}<input type={field.type || 'text'} value={company[field.key]} maxLength={field.maxLength} required={options.include_company && field.key !== 'company_email'} disabled={state === 'saving' || !options.include_company} onChange={event => updateCompany(field.key, event.target.value)} /></label>)}
              </div>
            </section>

            <section className="client-offers-card client-offers-step" aria-labelledby="client-offers-recipient-title">
              <div className="client-offers-step-heading">
                <span className="client-offers-number">3</span>
                <span className="client-offers-step-icon"><UserRound size={21} aria-hidden="true" /></span>
                <div className="client-offers-step-title"><h4 id="client-offers-recipient-title">Informacje o kliencie</h4><p>{options.include_recipient ? 'Wpisz dane adresata widoczne w ofercie.' : 'Cały blok adresata zostanie usunięty z oferty.'}</p></div>
                <label className="client-offers-toggle"><input type="checkbox" aria-label="Uwzględnij informacje o kliencie" checked={options.include_recipient} disabled={state === 'saving'} onChange={event => updateOption('include_recipient', event.target.checked)} /><span className="client-offers-toggle-track" aria-hidden="true" /><span>{options.include_recipient ? 'Włączone' : 'Wyłączone'}</span></label>
                <button className={`client-offers-collapse ${expandedSections.recipient ? 'open' : ''}`} type="button" aria-label={`${expandedSections.recipient ? 'Zwiń' : 'Rozwiń'} informacje o kliencie`} aria-expanded={expandedSections.recipient} aria-controls="client-offers-recipient-fields" onClick={() => toggleSection('recipient')}><ChevronDown size={20} aria-hidden="true" /></button>
              </div>
              <div id="client-offers-recipient-fields" hidden={!expandedSections.recipient} className={`client-offers-grid ${!options.include_recipient ? 'client-offers-grid-disabled' : ''}`}>
                <label>Nazwa klienta<input value={client.name} maxLength={150} disabled={state === 'saving' || !options.include_recipient} onChange={event => updateClient('name', event.target.value)} /></label>
                {fields.map(field => <label key={field.key}>{field.label}<input type={field.type || 'text'} value={client[field.key]} maxLength={field.maxLength} disabled={state === 'saving' || !options.include_recipient} onChange={event => updateClient(field.key, event.target.value)} /></label>)}
              </div>
            </section>
            <section className="client-offers-card client-offers-step" aria-labelledby="client-offers-translation-title">
              <div className="client-offers-step-heading">
                <span className="client-offers-number">4</span>
                <span className="client-offers-step-icon"><Languages size={21} aria-hidden="true" /></span>
                <div className="client-offers-step-title"><h4 id="client-offers-translation-title">Tłumaczenia</h4><p>Edytuj tekst źródłowy i tłumaczenie opisu oraz każdej konstrukcji.</p></div>
                <button className={`client-offers-collapse ${expandedSections.translation ? 'open' : ''}`} type="button" aria-label={`${expandedSections.translation ? 'Zwiń' : 'Rozwiń'} tłumaczenia`} aria-expanded={expandedSections.translation} aria-controls="client-offers-translation-fields" onClick={() => toggleSection('translation')}><ChevronDown size={20} aria-hidden="true" /></button>
              </div>
              <div id="client-offers-translation-fields" hidden={!expandedSections.translation}>
                <div className="client-offers-translation-toolbar">
                  <label>Język docelowy
                    <select value={targetLanguage} disabled={state === 'saving' || translating} onChange={event => changeLanguage(event.target.value)}>
                      <option value="de">Niemiecki</option>
                      <option value="nl">Holenderski</option>
                    </select>
                  </label>
                  <button type="button" onClick={translate} disabled={state === 'saving' || translating || !translationRows.length || translationRows.some(row => !row.original.trim())}>
                    <Languages size={17} aria-hidden="true" />{translating ? 'Tłumaczenie…' : 'Przetłumacz'}
                  </button>
                </div>
                {translationStatus && <p className={`client-offers-translation-status ${translationError ? 'error' : ''}`} role="status">{translationStatus}</p>}
                <div className="client-offers-translation-columns" aria-hidden="true"><span>Tekst oryginalny</span><span>Tłumaczenie do PDF</span></div>
                <div className="client-offers-translation-list">
                  {translationRows.map((row, index) => {
                    const group = row.construction === 'oferta' ? 'Opis oferty' : `Konstrukcja ${row.construction}`;
                    const previous = translationRows[index - 1];
                    const previousGroup = previous && (previous.construction === 'oferta' ? 'Opis oferty' : `Konstrukcja ${previous.construction}`);
                    return <div className="client-offers-translation-item" key={row.id}>
                      {group !== previousGroup && <h5>{group}</h5>}
                      <div className="client-offers-translation-pair">
                        <label><span>{TRANSLATION_KINDS[row.kind] || 'Tekst'} · oryginał</span><textarea value={row.original} rows={Math.max(3, Math.min(8, row.original.split('\n').length))} maxLength={20000} disabled={state === 'saving' || translating} onChange={event => updateTranslation(row.id, 'original', event.target.value)} /></label>
                        <label><span>{TRANSLATION_KINDS[row.kind] || 'Tekst'} · tłumaczenie</span><textarea value={row.translated} rows={Math.max(3, Math.min(8, row.translated.split('\n').length))} maxLength={20000} disabled={state === 'saving' || translating} onChange={event => updateTranslation(row.id, 'translated', event.target.value)} /></label>
                      </div>
                    </div>;
                  })}
                </div>
              </div>
            </section>
            <ClientOfferPricingSection analysis={pricingAnalysis} error={pricingError}
              settings={pricingSettings} evaluation={pricingEvaluation}
              disabled={state === 'saving'} onChange={updatePricing}
              expanded={expandedSections.pricing} onToggle={() => toggleSection('pricing')} />
            <ClientOfferMontageSection positions={montagePositions} enabled={montageEnabled}
              rows={montageRows} disabled={state === 'saving'}
              expanded={expandedSections.montage} onToggle={() => toggleSection('montage')}
              onEnabled={updateMontageEnabled} onRowsChange={updateMontageRows} />
            <div className="client-offers-actions"><button type="submit" disabled={state === 'saving' || translating || pricingEvaluation.invalid || montageInvalid}>{state === 'saving' ? 'Przygotowywanie oferty…' : 'Przygotuj ofertę PDF'}</button></div>
          </form>
        )}

        {previewUrl && <section className="client-offers-card client-offers-result"><div className="client-offers-card-heading"><span className="client-offers-card-icon"><FileCheck2 size={23} aria-hidden="true" /></span><div><h3>Gotowa oferta</h3><p>Sprawdź podgląd, a następnie pobierz plik.</p></div></div><iframe className="client-offers-preview" title="Podgląd zmodyfikowanej oferty" src={previewUrl} /><div className="client-offers-actions"><a className="client-offers-download" href={previewUrl} download={`${file.name.replace(/\.pdf$/i, '')}_gotowa.pdf`}><Download size={18} aria-hidden="true" /> Pobierz ofertę PDF</a></div></section>}
      </div>
    </div>
  );
}
